import { LegalPageLayout } from '@/components/legal-page-layout'

export default function LegalPrivacy() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="July 19, 2025">
      <section className="mb-6">
        <h2 className="mb-2 text-xl">1. Introduction</h2>
        <p>
          Welcome to Git Digest! This Privacy Policy explains how we collect, use, disclose, and safeguard your
          information when you use our Service. When we say &quot;Company&quot;, &quot;we&quot;, &quot;our&quot;, or
          &quot;us&quot; in this document, we are referring to Sunergos IT LLC, the company behind Git Digest. When we
          say &quot;Service&quot;, we are referring to Git Digest. When we say &quot;You&quot; or &quot;your&quot;, we
          are referring to the people or organizations that use our Service.
        </p>
        <p className="mt-2">
          By using Git Digest, you agree to the collection and use of information in accordance with this policy. We
          will not use or share your information with anyone except as described in this Privacy Policy.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-xl">2. Information We Collect</h2>
        <p>We collect the following information to provide and improve our Service to you:</p>
        <ul className="mt-2 list-inside list-disc space-y-2">
          <li>
            GitHub account data: To provide our core service, we analyze data from your GitHub repositories that you
            explicitly grant us access to. It&apos;s important to note that we do not store any repository code in our
            database. We only process code patch changes and we don&apos;t store these in our database either.
            <ul className="ml-6 list-inside list-disc space-y-1">
              <li>GitHub information we store:</li>
              <ul className="ml-6 list-inside list-disc space-y-1">
                <li>
                  Repository data: ID, owner, name, description, URL, default branch, full name, created and updated
                  timestamps, and whether the repository is private
                </li>
                <li>
                  Commit data: URL, sha, message, commit date, any pull request data linked to the commit (number,
                  description, opened and merged timestamps), author (name and avatar URL), lines added, lines removed,
                  number of changed files, created and updated timestamps
                </li>
              </ul>
            </ul>
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-xl">3. Use of Data</h2>
        <p>Git Digest uses the collected data for various purposes:</p>
        <ul className="mt-2 list-inside list-disc space-y-2">
          <li>To provide and maintain our Service</li>
          <li>To notify you about changes to our Service</li>
          <li>To allow you to participate in interactive features of our Service when you choose to do so</li>
          <li>To provide customer support</li>
          <li>To gather analysis or valuable information so that we can improve our Service</li>
          <li>To monitor the usage of our Service</li>
          <li>To detect, prevent and address technical issues</li>
        </ul>
        <p className="mt-2">
          We use OpenAI&apos;s completion API to process code patch changes. OpenAI does not train their models on the
          code patch changes we send to their API.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-xl">4. Third-Party Services</h2>
        <p>We use the following third-party services to operate our Service:</p>
        <ul className="mt-2 list-inside list-disc space-y-2">
          <li>Stripe: For processing payments</li>
          <li>GitHub: For authentication and repository access</li>
          <li>Resend: For transactional email delivery</li>
          <li>Rybbit: For product analytics (pageviews and activation events)</li>
          <li>PostHog: For error tracking and product analytics</li>
        </ul>
        <p className="mt-2">
          These services may collect, use, and share your information. We encourage you to review the privacy policies
          of these third-party services.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-xl">5. Data Security</h2>
        <p>
          The security of your data is important to us, but remember that no method of transmission over the Internet or
          method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect
          your Personal Data, we cannot guarantee its absolute security.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-xl">6. Your Data Protection Rights</h2>
        <p>
          Git Digest aims to take reasonable steps to allow you to correct, amend, delete, or limit the use of your
          Personal Data. If you wish to be informed what Personal Data we hold about you and if you want it to be
          removed from our systems, please contact us.
        </p>
        <p className="mt-2">In certain circumstances, you have the following data protection rights:</p>
        <ul className="mt-2 list-inside list-disc space-y-2">
          <li>The right to access, update or to delete the information we have on you</li>
          <li>The right of rectification</li>
          <li>The right to object</li>
          <li>The right of restriction</li>
          <li>The right to data portability</li>
          <li>The right to withdraw consent</li>
        </ul>
      </section>

      <section className="mt-8 mb-6">
        <h2 className="mb-2 text-xl">8. Data Retention Policy</h2>
        <p>
          We only maintain minimal, necessary data within our systems. The data we do keep (such as repository metadata,
          commit metadata, and related analytics) is retained as long as you have an active account on Git Digest or
          until it is no longer relevant for providing our Service. If you request account deletion, we proceed to remove
          associated data after a grace period to allow for any necessary dispute resolution or debugging.
        </p>
        <p className="mt-2">
          This retention approach is consistent with our codebase logic, which focuses on processing Git repository
          commits in real time without storing entire code or patch changes in our database.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-xl">9. Data Archival and Removal Policy</h2>
        <p>
          In cases where data is no longer needed for immediate processing, it may be archived in a secure location with
          restricted access. Archival primarily pertains to logs and metadata used for system debugging and performance
          metrics. Logs containing sensitive information are purged or anonymized in accordance with our internal
          guidelines.
        </p>
        <p className="mt-2">
          When you request data deletion or close your account, we will remove personal data from our active database.
          Some archival logs, which do not include sensitive or personal information, may be retained for operational or
          compliance purposes.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-xl">10. Data Storage Policy</h2>
        <p>
          We store your data in an encrypted database, and only essential repository details and commit metadata are
          persisted. Our codebase ensures that we do not store any repository code; only references to repository
          owners, commit SHAs, and relevant metadata are kept for analytics and summarization.
        </p>
        <p className="mt-2">
          To further mitigate risks, we rely on established cloud service providers that maintain industry-standard
          security measures.
        </p>
        <p className="mt-2">
          By following a minimal data retention approach on any stored text, we aim to protect your data and reduce the
          possibility of unauthorized disclosure.
        </p>
      </section>

      <section>
        <h2 className="mb-2 text-xl">11. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy, please contact us at{' '}
          <a href="mailto:support@gitdigest.ai" className="decoration-golden-500 hover:text-golden-500 underline">
            support@gitdigest.ai
          </a>
          .
        </p>
      </section>
    </LegalPageLayout>
  )
}
